import { Typography, Stack, Icon, Box, IconButton } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../../utils/numberHelper';
import { useContext, useState } from 'react';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { CurrentPayPlan, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import {
  calculateFamilyUpgradeCost,
  getAssociateOriginalPrice,
  getAssociateDiscountedPrice,
  getAssociatePrices,
  getFamilyUpgradeCosts,
  convertBillPlanToTerm,
  formatFullName,
} from '../../utils/helpers';
import { AddOn } from './AddOn';
import { TooltipModalContentProps } from '../TooltipModal';
import { validateAssociates } from '../../utils/validations';
import { useHistory } from 'react-router-dom';
import { DiscountRule, Pages } from '@lib/types';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../RemoveWarningModal';
import { AdditionalMemberTooltip } from './AdditionalMemberTooltip';
import { AddedInfoCard } from './AddedInfoCard';
import { associateTypeMap } from '../../utils/stringHelper';

export const FamilyMemberAddOn = () => {
  const { membershipInfo, sessionData, updateAssociates } = useContext(MembershipAppStateContext);
  const { billPlan } = sessionData.formData;
  const priceSuffix = billPlan == CurrentPayPlan.RECURRINGMONTHLY ? 'month' : 'year';
  const [memAddOnTooltipInfo, setMemAddOnTooltipInfo] = useState<TooltipModalContentProps | null>(null);
  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const originalFamilyUpgradeFee = getFamilyUpgradeCosts(membershipInfo!, sessionData).baseCost;
  const discountedFamilyUpgradeFee = calculateFamilyUpgradeCost(membershipInfo!, sessionData);
  const history = useHistory();
  const associates = sessionData?.formData.associates ?? [];
  const { hasReachedMaxAssociates } = validateAssociates(membershipInfo!, sessionData.formData);
  //check if there's free associate
  const membershipLevel = sessionData.formData.membershipLevel;
  const ml =
    membershipLevel == undefined ||
    membershipLevel == MembershipLevel.CLASSIC_RV ||
    membershipLevel == MembershipLevel.CLASSIC
      ? MembershipLevel.CLASSIC
      : membershipLevel == MembershipLevel.PLUS_RV || membershipLevel == MembershipLevel.PLUS
        ? MembershipLevel.PLUS
        : membershipLevel == MembershipLevel.PREMIER_RV || membershipLevel == MembershipLevel.PREMIER
          ? MembershipLevel.PREMIER
          : membershipLevel;
  const term = convertBillPlanToTerm(sessionData.formData?.billPlan!);
  const hasFreeAssociate = membershipInfo?.membershipCost?.[ml]?.[term]?.membershipDiscounts?.find(
    (rule) => rule?.rule == DiscountRule.FREE_ASSOCIATE && rule.benefitLevel === ml
  );
  const freeAssociateCount = hasFreeAssociate?.count || 0;
  const numberOfAssociates = sessionData.formData.associates?.length || 0;
  const { isPriceCartLoading } = useContext(MembershipAppStateContext);
  return (
    <>
      <Box className="membership-add-ons-container">
        <Box
          className="benefit-add-ons"
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '24px' }}
        >
          <Stack direction="row" alignItems="center" sx={{ gap: '0.5rem' }}>
            <Icon iconName="user-group-duotone" sx={{ color: '#0C7A93', fontSize: '1.5rem' }} className="user-icon" />
            <Typography className="member-add-ons-title" variant="b1Medium">
              Family members
            </Typography>
          </Stack>
          <IconButton
            aria-label="membership addon info"
            className="add-member-info-icon"
            onClick={() => {
              setMemAddOnTooltipInfo({
                tooltipTitle: 'Add a member',
              });
            }}
          >
            <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
          </IconButton>
        </Box>

        {originalFamilyUpgradeFee > 0 ? (
          <Box marginBottom={'0.5rem'}>
            <Typography className="family-upgrade-warning" variant="b3Regular">
              There is a {numberHelper.formatMoney(originalFamilyUpgradeFee)} fee for a family upgrade
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {associates.map((associate, i) => (
          <AddedInfoCard
            key={i}
            title={formatFullName(associate.firstName, associate.lastName)}
            content={associateTypeMap[associate.roleType]}
            editButtonOnClick={() => {
              history.push({
                pathname: Pages.AddAssociatePage,
                state: { associate: associate, isEditing: true },
              });
            }}
            deleteButtonOnClick={() => {
              setDeleteInfo({
                title: `Are you sure you want to remove ${formatFullName(associate.firstName, associate.lastName)} from your membership?`,
                confirmRemove: () => {
                  updateAssociates('remove', associate);
                  // .catch(() => {});
                },
              });
            }}
            originalPrice={getAssociateOriginalPrice(sessionData, associate)}
            priceAfterDiscount={getAssociateDiscountedPrice(sessionData, associate)}
            priceSuffix={priceSuffix}
            className={`associate-${i}`}
          />
        ))}

        {originalFamilyUpgradeFee && associates.length > 0 ? (
          <AddedInfoCard
            key={'family-upgrade-fee'}
            title={'Family upgrade fee'}
            originalPrice={originalFamilyUpgradeFee}
            priceAfterDiscount={discountedFamilyUpgradeFee}
            priceSuffix={priceSuffix}
            className={'family-upgrade-fee'}
          />
        ) : (
          <></>
        )}

        {hasReachedMaxAssociates ? (
          <></>
        ) : (
          <Box marginTop={'1rem'}>
            <AddOn
              onAdd={() => {
                history.push(Pages.AddAssociatePage);
              }}
              text="Add a member"
              discountedPrice={hasFreeAssociate && numberOfAssociates < freeAssociateCount ? 0 : undefined}
              price={getAssociatePrices(membershipInfo!, sessionData)}
              icon="user-plus-regular"
              type={'associate'}
              priceSuffix={priceSuffix}
              isPriceCartLoading={isPriceCartLoading}
            />
          </Box>
        )}
      </Box>

      <AdditionalMemberTooltip
        memAddOnTooltipInfo={memAddOnTooltipInfo}
        setMemAddOnTooltipInfo={setMemAddOnTooltipInfo}
        roles={membershipInfo!.roles}
      />
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />
    </>
  );
};
